import Phaser from 'phaser';
import { PRIVACY_URL } from '../config';
import { CloudSync } from '../game/CloudSync';
import { t } from '../game/i18n';
import { addBackButton } from '../ui/BackButton';
import { drawGradientBackground } from '../ui/Background';
import { viewport } from '../ui/viewport';

const ERR: Record<string, string> = {
  username_taken: 'auth.errTaken', username_short: 'auth.errShortUser', password_short: 'auth.errShortPass',
  not_found: 'auth.errNotFound', bad_password: 'auth.errBadPass',
};

/**
 * Simple local account login/registration (username + password, no email). DOM form anchored in
 * the upper third so the mobile keyboard never covers it (same approach as NameEntry).
 */
export class LocalAuth extends Phaser.Scene {
  private form?: HTMLFormElement;
  private errText?: Phaser.GameObjects.Text;
  private busy = false;

  constructor() {
    super('LocalAuth');
  }

  create(): void {
    const { cx, h, s } = viewport(this);
    drawGradientBackground(this);
    addBackButton(this, s, () => { this.cleanup(); this.scene.start('Account'); });

    this.add.text(cx, h * 0.1, `👤 ${t('account.title')}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(28 * s)}px`, color: '#f4f7ff', fontStyle: 'bold',
    }).setOrigin(0.5).setShadow(0, 3, '#000000aa', 6);
    this.add.text(cx, h * 0.1 + Math.max(26, 30 * s), t('auth.localHint'), {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(12, Math.round(14 * s))}px`, color: '#9fb0e0',
      align: 'center', wordWrap: { width: Math.min(460, this.scale.width * 0.8) },
    }).setOrigin(0.5);

    const form = document.createElement('form');
    Object.assign(form.style, {
      position: 'fixed', left: '50%', top: '32%', transform: 'translateX(-50%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
      width: 'min(86vw, 380px)', zIndex: '30',
    } satisfies Partial<CSSStyleDeclaration>);

    const mkInput = (ph: string, type: string, hint: string): HTMLInputElement => {
      const i = document.createElement('input');
      i.type = type; i.placeholder = ph; i.maxLength = 32; i.autocomplete = type === 'password' ? 'current-password' : 'username';
      i.enterKeyHint = hint as HTMLInputElement['enterKeyHint'];
      Object.assign(i.style, {
        width: '100%', padding: '12px 14px', fontSize: '18px', fontFamily: '"Twemoji Country Flags", system-ui, sans-serif',
        borderRadius: '12px', border: '2px solid #59d2ff', background: '#0b1020', color: '#f4f7ff',
        textAlign: 'center', outline: 'none', boxSizing: 'border-box',
      } satisfies Partial<CSSStyleDeclaration>);
      return i;
    };
    const user = mkInput(t('auth.username'), 'text', 'next');
    const pass = mkInput(t('auth.password'), 'password', 'done');

    const row = document.createElement('div');
    Object.assign(row.style, { display: 'flex', gap: '10px', width: '100%' } satisfies Partial<CSSStyleDeclaration>);
    const mkBtn = (label: string, bg: string, mode: 'login' | 'register'): HTMLButtonElement => {
      const b = document.createElement('button');
      b.type = 'button'; b.textContent = label;
      Object.assign(b.style, {
        flex: '1', padding: '12px 8px', fontSize: '17px', fontWeight: '800',
        fontFamily: '"Twemoji Country Flags", system-ui, sans-serif', borderRadius: '12px', border: 'none',
        background: bg, color: '#0b1020', cursor: 'pointer', touchAction: 'manipulation',
      } satisfies Partial<CSSStyleDeclaration>);
      b.addEventListener('click', () => this.submit(mode, user.value, pass.value));
      return b;
    };
    row.appendChild(mkBtn(t('auth.login'), '#59d2ff', 'login'));
    row.appendChild(mkBtn(t('auth.register'), '#4ade80', 'register'));

    form.appendChild(user); form.appendChild(pass); form.appendChild(row);
    form.addEventListener('submit', (e) => { e.preventDefault(); this.submit('login', user.value, pass.value); });
    document.body.appendChild(form);
    this.form = form;
    setTimeout(() => user.focus(), 60);

    this.errText = this.add.text(cx, h * 0.84, '', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(15 * s)}px`, color: '#ff8f9c', fontStyle: 'bold' }).setOrigin(0.5);

    // Privacy/terms link — registering an account implies a data-handling notice.
    this.add.text(cx, h - 12 * s, t('legal.privacy'), {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(11, Math.round(12 * s))}px`, color: '#7c88b4',
    }).setOrigin(0.5, 1).setInteractive({ useHandCursor: true })
      .on(Phaser.Input.Events.POINTER_DOWN, () => { try { window.open(PRIVACY_URL, '_blank', 'noopener'); } catch { /* ignore */ } });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanup());
  }

  private submit(mode: 'login' | 'register', username: string, password: string): void {
    if (this.busy) return;
    if (username.trim().length < 3) { this.errText?.setText(t('auth.errShortUser')); return; }
    if (password.length < 4) { this.errText?.setText(t('auth.errShortPass')); return; }
    this.busy = true;
    this.errText?.setText(t('arena.loading'));
    void CloudSync.localAuth(mode, username.trim(), password).then((err) => {
      this.busy = false;
      if (err) { this.errText?.setText(ERR[err] ? t(ERR[err]!) : t('arena.offline')); return; }
      this.cleanup();
      this.scene.start('Account'); // signed-in now (SaveManager merged via onChange)
    });
  }

  private cleanup(): void {
    this.form?.remove();
    this.form = undefined;
  }
}
